import { useCallback, useEffect, useRef } from "react";

import { CustomerAddress } from "@/networking/addresses.api";

export default function useSetDefaultAddress(
  customerAddresses: CustomerAddress[],
  cartCountry: string | undefined,
  selectedShippingAddressId: number | null,
  handleSelect: (address: CustomerAddress) => void,
) {
  const failedAddressIds = useRef<Set<number>>(new Set());
  const hasAttemptedInThisCycle = useRef<boolean>(false);
  const isSelecting = useRef<boolean>(false);

  const selectAddress = useCallback(
    async (address: CustomerAddress) => {
      if (failedAddressIds.current.has(address.id) || isSelecting.current) {
        return;
      }

      isSelecting.current = true;
      hasAttemptedInThisCycle.current = true;

      try {
        await handleSelect(address);
        failedAddressIds.current.delete(address.id);
      } catch (error) {
        failedAddressIds.current.add(address.id);
        console.error("Failed to set address to cart:", error);
      } finally {
        isSelecting.current = false;
      }
    },
    [handleSelect],
  );

  useEffect(() => {
    if (
      selectedShippingAddressId !== null &&
      customerAddresses.some(
        (address) => address.id === selectedShippingAddressId,
      )
    ) {
      failedAddressIds.current.clear();
      hasAttemptedInThisCycle.current = false;
      return;
    }

    if (isSelecting.current || hasAttemptedInThisCycle.current) {
      return;
    }

    if (!customerAddresses?.length || !cartCountry) {
      return;
    }

    const defaultAddress = customerAddresses.find((address) => address.default);
    const fallbackAddress = customerAddresses.find(
      (address) => address.country_code === cartCountry,
    );

    const hasValidAddressSelected =
      selectedShippingAddressId !== null &&
      customerAddresses.some(
        (address) => address.id === selectedShippingAddressId,
      );
    const needsAddressSelection = !hasValidAddressSelected;

    if (
      defaultAddress &&
      cartCountry === defaultAddress.country_code &&
      needsAddressSelection
    ) {
      if (!failedAddressIds.current.has(defaultAddress.id)) {
        selectAddress(defaultAddress);
      }
    } else if (fallbackAddress && needsAddressSelection) {
      if (!failedAddressIds.current.has(fallbackAddress.id)) {
        selectAddress(fallbackAddress);
      }
    } else if (defaultAddress && needsAddressSelection) {
      if (!failedAddressIds.current.has(defaultAddress.id)) {
        selectAddress(defaultAddress);
      }
    } else if (customerAddresses[0] && needsAddressSelection) {
      if (!failedAddressIds.current.has(customerAddresses[0].id)) {
        selectAddress(customerAddresses[0]);
      }
    }
  }, [
    customerAddresses,
    cartCountry,
    selectAddress,
    selectedShippingAddressId,
  ]);
}
